import React, { useEffect, useRef, useState } from "react";
import * as faceapi from "face-api.js";
import axios from "axios";
import attendanceAPI from "../api/attendanceAPI";
import { saveFaceStatus as saveFaceStatusAPI } from "../api/faceStatusAPI";

// ── Config ────────────────────────────────────────────────────────────────────
const CHECK_INTERVAL_MS      = 10 * 60 * 1000; // 10 minutes
const MAX_MISS_BEFORE_LOGOUT = 2;               // consecutive misses before auto-logout

// ─────────────────────────────────────────────────────────────────────────────
// FaceWatcher — renders nothing, runs background face checks silently
// Every 10 min:
//   Face found  → reset miss counter → save "Present" status
//   No face     → increment counter → save "Not here" status
//                 After 2 consecutive misses → auto-logout + notify
// ─────────────────────────────────────────────────────────────────────────────
const FaceWatcher = ({ onStatusChange }) => {
  const [isModelsLoaded, setIsModelsLoaded] = useState(false);
  const [worker, setWorker]                 = useState(null);

  const missCountRef  = useRef(0);
  const loggedOutRef  = useRef(false);

  // ── Load model + worker ───────────────────────────────────────────────────
  useEffect(() => {
    const loadModels = async () => {
      const MODEL_URL = process.env.PUBLIC_URL + "/models";
      try {
        await faceapi.nets.tinyFaceDetector.loadFromUri(
          `${MODEL_URL}/tiny_face_detector`
        );
        console.log("✅ FaceWatcher: model loaded");
        setIsModelsLoaded(true);
      } catch (err) {
        console.error("❌ FaceWatcher: model load failed", err);
      }
    };

    loadModels();

    const saved = localStorage.getItem("worker");
    if (saved && saved !== "undefined") {
      setWorker(JSON.parse(saved));
    }
  }, []);

  // ── Save face status ──────────────────────────────────────────────────────
  const saveFaceStatus = async (status) => {
    try {
      await saveFaceStatusAPI(worker._id, status, worker.company, worker.team);
    } catch (err) {
      console.error("❌ FaceWatcher: saveFaceStatus error", err);
    }
  };

  // ── Auto-logout + backend notification ────────────────────────────────────
  const handleAutoLogout = async () => {
    console.log(`🔴 FaceWatcher: auto-logout for ${worker.username}`);

    // 1️⃣ Logout attendance
    try {
      await attendanceAPI.post("/face-logout", { workerId: worker._id });
      console.log("✅ FaceWatcher: attendance logged out");
    } catch (err) {
      if (err.response?.status !== 400) {
        console.error("❌ FaceWatcher: attendance logout failed", err);
      }
    }

    // 2️⃣ Backend saves "Auto Logged Out" status + notifies Office, HR, TL
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        "http://localhost:5000/api/face-status/auto-logout",
        { workerId: worker._id, company: worker.company, team: worker.team },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log("✅ FaceWatcher: auto-logout notification sent");
    } catch (err) {
      console.error("❌ FaceWatcher: auto-logout notification failed", err);
    }

    onStatusChange?.(
      `${worker.username}: Auto Logged Out after ${MAX_MISS_BEFORE_LOGOUT} missed checks`
    );
  };

  // ── Main interval ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isModelsLoaded || !worker) return;
    if (worker.role === "Office") return; // Office skipped

    const runCheck = async () => {
      let stream = null;
      try {
        // Open camera
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
        const video     = document.createElement("video");
        video.srcObject = stream;
        video.muted     = true;
        await video.play();

        // Warm-up
        await new Promise((r) => setTimeout(r, 800));

        // Capture frame
        const canvas  = document.createElement("canvas");
        canvas.width  = video.videoWidth  || 640;
        canvas.height = video.videoHeight || 480;
        canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

        // Detect face
        const detection = await faceapi.detectSingleFace(
          canvas,
          new faceapi.TinyFaceDetectorOptions({ inputSize: 512, scoreThreshold: 0.3 })
        );

        if (detection) {
          // ── Face found — reset ─────────────────────────────────────────
          console.log("😀 FaceWatcher: face detected");
          missCountRef.current = 0;
          loggedOutRef.current = false;
          await saveFaceStatus("Present");
          onStatusChange?.(`${worker.username}: Present`);
        } else {
          // ── No face ───────────────────────────────────────────────────
          missCountRef.current += 1;
          console.log(
            `🚨 FaceWatcher: no face — miss ${missCountRef.current}/${MAX_MISS_BEFORE_LOGOUT}`
          );

          await saveFaceStatus("Not here");
          onStatusChange?.(
            `${worker.username}: No Face (${missCountRef.current}/${MAX_MISS_BEFORE_LOGOUT})`
          );

          // Trigger auto-logout after MAX consecutive misses
          if (
            missCountRef.current >= MAX_MISS_BEFORE_LOGOUT &&
            !loggedOutRef.current
          ) {
            loggedOutRef.current = true;
            await handleAutoLogout();
          }
        }
      } catch (err) {
        console.error("❌ FaceWatcher check error:", err);
      } finally {
        if (stream) stream.getTracks().forEach((t) => t.stop());
      }
    };

    // Run immediately, then repeat
    runCheck();
    const interval = setInterval(runCheck, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);

  }, [isModelsLoaded, worker]);

  return null; // no UI
};

export default FaceWatcher;